import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

export const useFetchBillingInvoices = (claimId, opts = {}) => {
  return useQuery(
    ['billing-invoices', claimId],
    async () => {
      const res = await axios.get('/billing/invoices', { params: { claimId } });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000,
      enabled: !!claimId,
      ...opts,
    },
  );
};

export const useFetchBillingCongratulationsEmail = (claimId, opts = {}) => {
  return useQuery(
    ['billing-congratulations-email', claimId],
    async () => {
      const res = await axios.get(
        `/billing/claims/${claimId}/congratulations-email`,
      );
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000,
      enabled: !!claimId,
      ...opts,
    },
  );
};

export const useCreateInvoice = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (fields) => {
      const res = await axios.post(
        `/billing/claims/${claimId}/invoices`,
        fields,
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['billing-invoices', claimId]);
        queryClient.invalidateQueries([
          'billing-payment-plan-summary',
          claimId,
        ]);
        onSuccess?.(data);
      },
    },
  );
};

export const useFetchPaymentPlanSummary = (claimId, opts = {}) => {
  return useQuery(
    ['billing-payment-plan-summary', claimId],
    async () => {
      const res = await axios.get(
        `/billing/claims/${claimId}/payment-plan/summary`,
      );
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000,
      enabled: !!claimId,
      ...opts,
    },
  );
};

export const useUpdateInvoice = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ invoiceId, ...fields }) => {
      const res = await axios.patch(`/billing/invoices/${invoiceId}`, fields);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['billing-invoices', claimId]);
        onSuccess?.();
      },
    },
  );
};

export const useDeleteInvoice = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (invoiceId) => {
      const res = await axios.delete(`/billing/invoices/${invoiceId}`);
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['billing-invoices', claimId]);
        queryClient.invalidateQueries([
          'billing-payment-plan-summary',
          claimId,
        ]);
        onSuccess?.(data);
      },
    },
  );
};

export const useSendBillingCongratulationsEmail = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async () => {
      const res = await axios.post(
        `/billing/claims/${claimId}/congratulations-email/send`,
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([
          'billing-congratulations-email',
          claimId,
        ]);
        queryClient.invalidateQueries(['billing-invoices', claimId]);
        onSuccess?.(data);
      },
    },
  );
};

export const useConfirmInvoice = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (invoiceId) => {
      const res = await axios.post(`/billing/invoices/${invoiceId}/confirm`);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['billing-invoices', claimId]);
        onSuccess?.();
      },
    },
  );
};

const getReturnPath = () =>
  `${window.location.pathname}${window.location.search || ''}`;

const invalidatePaymentPlanQueries = (queryClient, claimId) => {
  queryClient.invalidateQueries(['billing-payment-plan-summary', claimId]);
  queryClient.invalidateQueries(['billing-invoices', claimId]);
};

export const useStartPaymentPlan = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      invoiceId,
      termMonths,
      downPaymentAmount,
      firstInstallmentDate,
      installmentDueDates,
      note,
      returnPath,
      paymentPlanAuthorization,
      paymentMethodSource,
    }) => {
      const res = await axios.post(
        `/billing/claims/${claimId}/payment-plan/start`,
        {
          invoiceId,
          termMonths,
          downPaymentAmount,
          firstInstallmentDate,
          installmentDueDates,
          note,
          returnPath: returnPath || getReturnPath(),
          paymentPlanAuthorization,
          paymentMethodSource,
        },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};

export const useUpdatePaymentPlanSetup = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      paymentPlanId,
      termMonths,
      downPaymentAmount,
      firstInstallmentDate,
      installmentDueDates,
      note,
      returnPath,
      paymentPlanAuthorization,
    }) => {
      const res = await axios.patch(
        `/billing/payment-plans/${paymentPlanId}/setup`,
        {
          termMonths,
          downPaymentAmount,
          firstInstallmentDate,
          installmentDueDates,
          note,
          returnPath: returnPath || getReturnPath(),
          paymentPlanAuthorization,
        },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};

export const useAuthorizePaymentPlanSetup = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ paymentPlanId, paymentPlanAuthorization }) => {
      const res = await axios.post(
        `/billing/payment-plans/${paymentPlanId}/authorization`,
        {
          paymentPlanAuthorization,
          returnPath: getReturnPath(),
        },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};

export const useSetPaymentPlanVisibility = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ enabled, note }) => {
      const res = await axios.post(
        `/billing/claims/${claimId}/payment-plan/visibility`,
        {
          enabled,
          note,
        },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};

export const useApprovePaymentPlan = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ paymentPlanId, note }) => {
      const res = await axios.post(
        `/billing/payment-plans/${paymentPlanId}/approve`,
        {
          note,
          returnPath: getReturnPath(),
        },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};

export const useRejectPaymentPlan = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ paymentPlanId, note }) => {
      const res = await axios.post(
        `/billing/payment-plans/${paymentPlanId}/reject`,
        {
          note,
        },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};

export const useCreatePaymentPlanBillingMethodSession = (
  claimId,
  onSuccess,
) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ paymentPlanId }) => {
      const res = await axios.post(
        `/billing/payment-plans/${paymentPlanId}/billing-method-session`,
        { returnPath: getReturnPath() },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};

export const useCreatePaymentPlanPayoffSession = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ paymentPlanId }) => {
      const res = await axios.post(
        `/billing/payment-plans/${paymentPlanId}/payoff-session`,
        { returnPath: getReturnPath() },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};

export const useCancelPaymentPlan = (claimId, onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ paymentPlanId, note }) => {
      const res = await axios.post(
        `/billing/payment-plans/${paymentPlanId}/cancel`,
        {
          note,
        },
      );
      return res.data;
    },
    {
      onSuccess: (data) => {
        invalidatePaymentPlanQueries(queryClient, claimId);
        onSuccess?.(data);
      },
    },
  );
};
